import React, { useState } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, ToggleButtonGroup, ToggleButton, Chip,
} from '@mui/material';
import {
  School as SchoolIcon,
  TrendingUp as FlowIcon,
  People as GenderIcon,
  Business as InfraIcon,
  GroupWork as StaffIcon,
  Timeline as CohortIcon,
  ShowChart as ForecastIcon,
  Category as ClusterIcon,
  Person as TeacherIcon,
  CalendarMonth as SeasonIcon,
  Warning as AnomalyIcon,
  Leaderboard as BenchmarkIcon,
} from '@mui/icons-material';

// Core tabs
import SchoolPerformanceTab from '../../components/MLAnalytics/SchoolPerformanceTab';
import GradeFlowTab from '../../components/MLAnalytics/GradeFlowTab';
import GenderEquityTab from '../../components/MLAnalytics/GenderEquityTab';

// Advanced tabs
import InfrastructureCorrelationTab from '../../components/MLAnalytics/InfrastructureCorrelationTab';
import StaffingOptimizationTab from '../../components/MLAnalytics/StaffingOptimizationTab';
import CohortSurvivalTab from '../../components/MLAnalytics/CohortSurvivalTab';
import EnrollmentForecastTab from '../../components/MLAnalytics/EnrollmentForecastTab';
import AttendanceClusterTab from '../../components/MLAnalytics/AttendanceClusterTab';
import TeacherImpactTab from '../../components/MLAnalytics/TeacherImpactTab';
import SeasonalPatternsTab from '../../components/MLAnalytics/SeasonalPatternsTab';
import AnomalyDetectionTab from '../../components/MLAnalytics/AnomalyDetectionTab';
import DistrictBenchmarkingTab from '../../components/MLAnalytics/DistrictBenchmarkingTab';

interface TabPanelProps {
  children?: React.ReactNode;
  active: boolean;
}

function TabPanel({ children, active }: TabPanelProps) {
  if (!active) return null;
  return <Box sx={{ pt: 3 }}>{children}</Box>;
}

// Category definitions
const CATEGORIES = [
  {
    id: 'school',
    label: 'School & Infrastructure',
    icon: <SchoolIcon />,
    color: '#1976d2',
    tabs: [
      { label: 'School Performance Index', icon: <SchoolIcon />, component: SchoolPerformanceTab },
      { label: 'Infrastructure Impact', icon: <InfraIcon />, component: InfrastructureCorrelationTab },
      { label: 'Staffing Optimization', icon: <StaffIcon />, component: StaffingOptimizationTab },
    ],
  },
  {
    id: 'progression',
    label: 'Student Progression',
    icon: <FlowIcon />,
    color: '#388e3c',
    tabs: [
      { label: 'Grade Flow & Cohort', icon: <FlowIcon />, component: GradeFlowTab },
      { label: 'Cohort Survival', icon: <CohortIcon />, component: CohortSurvivalTab },
      { label: 'Enrollment Forecasting', icon: <ForecastIcon />, component: EnrollmentForecastTab },
    ],
  },
  {
    id: 'equity',
    label: 'Equity & Behavior',
    icon: <GenderIcon />,
    color: '#7b1fa2',
    tabs: [
      { label: 'Gender Equity', icon: <GenderIcon />, component: GenderEquityTab },
      { label: 'Attendance Clustering', icon: <ClusterIcon />, component: AttendanceClusterTab },
      { label: 'Seasonal Patterns', icon: <SeasonIcon />, component: SeasonalPatternsTab },
    ],
  },
  {
    id: 'monitoring',
    label: 'Performance & Monitoring',
    icon: <BenchmarkIcon />,
    color: '#e65100',
    tabs: [
      { label: 'District Benchmarking', icon: <BenchmarkIcon />, component: DistrictBenchmarkingTab },
      { label: 'Teacher Absence Impact', icon: <TeacherIcon />, component: TeacherImpactTab },
      { label: 'Anomaly Detection', icon: <AnomalyIcon />, component: AnomalyDetectionTab },
    ],
  },
];

const LearningAnalytics: React.FC = () => {
  const [categoryIndex, setCategoryIndex] = useState(0);
  const [tabIndex, setTabIndex] = useState(0);

  const activeCategory = CATEGORIES[categoryIndex];

  const handleCategoryChange = (_: React.MouseEvent<HTMLElement>, newCategory: number | null) => {
    if (newCategory !== null) {
      setCategoryIndex(newCategory);
      setTabIndex(0); // Reset tab when switching categories
    }
  };

  const ActiveComponent = activeCategory.tabs[tabIndex]?.component;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Learning Analytics
      </Typography>
      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        Comprehensive analytics across 12 dimensions — school performance, student progression,
        equity, and system monitoring.
      </Typography>

      {/* Category Toggle Buttons */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <ToggleButtonGroup
          value={categoryIndex}
          exclusive
          onChange={handleCategoryChange}
          size="small"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            '& .MuiToggleButton-root': {
              flex: '1 1 auto',
              minWidth: 180,
              borderRadius: '8px !important',
              border: '1px solid rgba(0,0,0,0.12) !important',
              textTransform: 'none',
              py: 1,
            },
            '& .MuiToggleButton-root.Mui-selected': {
              backgroundColor: `${activeCategory.color}15`,
              borderColor: `${activeCategory.color} !important`,
              color: activeCategory.color,
            },
          }}
        >
          {CATEGORIES.map((cat, idx) => (
            <ToggleButton key={cat.id} value={idx}>
              <Box display="flex" alignItems="center" gap={1}>
                {cat.icon}
                <Typography variant="body2" fontWeight={categoryIndex === idx ? 'bold' : 'normal'}>
                  {cat.label}
                </Typography>
                <Chip label={cat.tabs.length} size="small" variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }} />
              </Box>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Paper>

      {/* Feature Tabs within selected category */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, newValue) => setTabIndex(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          indicatorColor="primary"
          textColor="primary"
          sx={{
            '& .MuiTab-root': { textTransform: 'none', minHeight: 48 },
            '& .MuiTabs-indicator': { backgroundColor: activeCategory.color },
            '& .Mui-selected': { color: `${activeCategory.color} !important` },
          }}
        >
          {activeCategory.tabs.map((tab, idx) => (
            <Tab
              key={idx}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Active Tab Content */}
      <TabPanel active={true}>
        {ActiveComponent && <ActiveComponent />}
      </TabPanel>
    </Box>
  );
};

export default LearningAnalytics;
